import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { testimonials, stats, milestones } from "@/mocks/testimonials";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setFormStatus("success");
      setFormMessage("提交成功！我们会尽快与您联系。");
      form.reset();
      return;
    }

    setFormStatus("submitting");
    setFormMessage("");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9qronfnahb7382oveag", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== "phone_alt")
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || "";

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        setFormMessage("提交成功！我们会尽快与您联系。");
        form.reset();
      } else if (serverMsg.includes("spam") || serverMsg.includes("form data is spam")) {
        setFormStatus("error");
        setFormMessage(serverMsg || "提交失败，请稍后重试。");
      } else {
        setFormStatus("error");
        setFormMessage(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setFormStatus("error");
      setFormMessage("网络错误，请稍后重试。");
    } finally {
      if (formStatus === "submitting") {
        // avoid overwriting on fast responses
      }
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于黑料社区",
    description: "黑料社区品牌介绍、发展历程、用户口碑与联系方式",
    url: "https://heiliao.community/about",
    inLanguage: "zh-CN",
    isPartOf: {
      "@type": "WebSite",
      name: "黑料社区",
      url: "https://heiliao.community",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "黑料社区的内容是否真实可靠？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "黑料社区采用多层级内容审核机制，每篇爆料都需要提供相应证据支撑。同时我们建立了专业的调查团队，对重要爆料进行独立核实。",
        },
      },
      {
        "@type": "Question",
        name: "如何在黑料社区提交爆料？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "您可以通过网站顶部的「提交爆料」按钮或联系页面提交您的内幕线索。我们严格保护爆料人隐私，支持匿名爆料。",
        },
      },
      {
        "@type": "Question",
        name: "爆料人的隐私如何保护？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "我们采用端到端加密技术保护爆料人信息，爆料人可选择完全匿名。社区从不要求爆料人提供真实身份信息，所有沟通记录均加密存储。",
        },
      },
      {
        "@type": "Question",
        name: "黑料社区的盈利模式是什么？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "黑料社区坚持独立运营，不接受影响内容独立性的商业合作。收入来源包括用户自愿捐赠和会员订阅服务，所有收入均用于维持平台运营和调查团队建设。",
        },
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Navbar />

      <main className="pt-20">
        <div className="container-custom py-8">
          <Breadcrumb
            items={[
              { name: "首页", path: "/" },
              { name: "关于我们" },
            ]}
          />
        </div>

        {/* Brand Introduction */}
        <section id="brand" className="py-14 md:py-20">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-500/10">
                  <i className="ri-spy-fill text-primary-400 text-3xl"></i>
                </div>
                <h1 className="font-heading font-black text-3xl md:text-4xl text-foreground-100 mb-4">
                  关于黑料社区
                </h1>
                <p className="text-foreground-400 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                  黑料社区创立于2021年，由三位资深调查记者联合创立。
                  我们致力于打造一个不受商业利益左右的独立真相平台，
                  让每一个值得被听见的声音都能被世界听见。
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
                {stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="text-center p-5 bg-background-100 rounded-lg"
                  >
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-500/10">
                      <i className={`${stat.icon} text-primary-400 text-lg`}></i>
                    </div>
                    <div className="font-heading font-bold text-2xl text-foreground-100 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-foreground-500 text-xs">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Mission */}
              <div className="bg-background-100 rounded-lg p-6 md:p-10">
                <h2 className="font-heading font-bold text-xl text-foreground-100 mb-4">
                  我们的使命
                </h2>
                <p className="text-foreground-400 leading-relaxed mb-4">
                  在信息爆炸的时代，真相反而越来越稀缺。商业利益驱动的媒体环境让许多重要故事被埋没，
                  公关力量让不利信息被清理，算法推荐让用户困在信息茧房。
                </p>
                <p className="text-foreground-400 leading-relaxed">
                  黑料社区的存在，就是为了打破这些壁垒。我们相信，每一个内幕都值得被看见，
                  每一个真相都值得被传播。我们将继续坚持独立、客观、深度的调查报道，
                  为追求真相的读者提供第一手情报资讯。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section id="milestones" className="py-14 md:py-20 bg-background-100/50">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-100 text-center mb-12">
                发展历程
              </h2>
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-background-200/60 -translate-x-1/2"></div>

                <div className="flex flex-col gap-8">
                  {milestones.map((item, index) => (
                    <div
                      key={item.year}
                      className={`relative flex flex-col md:flex-row gap-4 ${
                        index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                      }`}
                    >
                      {/* Dot */}
                      <div className="absolute left-[19px] md:left-1/2 top-6 w-4 h-4 -translate-x-1/2 rounded-full bg-primary-500 border-4 border-background-50 z-10"></div>

                      <div className="flex-1 md:w-1/2 pl-12 md:pl-0">
                        <div className={`${index % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`}>
                          <div
                            className={`inline-block p-5 bg-background-100 rounded-lg ${
                              index % 2 === 0 ? "md:text-right" : ""
                            }`}
                          >
                            <span className="inline-block px-3 py-1 bg-primary-500/10 text-primary-400 text-xs font-bold rounded-full mb-2">
                              {item.year}
                            </span>
                            <h3 className="font-heading font-bold text-foreground-100 mb-1">{item.title}</h3>
                            <p className="text-foreground-500 text-sm leading-relaxed">{item.description}</p>
                          </div>
                        </div>
                      </div>

                      {/* Spacer for alternating layout */}
                      <div className="hidden md:block flex-1 md:w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-14 md:py-20">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-100 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-sm md:text-base">
                来自数百万用户的真实评价
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 bg-background-100 rounded-lg flex flex-col">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-foreground-400 text-sm leading-relaxed mb-4 flex-1">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div>
                    <div className="text-foreground-200 text-sm font-medium">{t.name}</div>
                    <div className="flex items-center gap-2 text-foreground-600 text-xs mt-0.5">
                      <span>{t.location}</span>
                      <span>·</span>
                      <span>{t.joinedAt}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-100/50">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-100 text-center mb-12">
                常见问题
              </h2>
              <div className="flex flex-col gap-4">
                {[
                  { q: "黑料社区的内容是否真实可靠？", a: "黑料社区采用多层级内容审核机制，每篇爆料都需要提供相应证据支撑。同时我们建立了专业的调查团队，对重要爆料进行独立核实。我们也会标注内容的可信度等级，帮助读者判断。" },
                  { q: "如何在黑料社区提交爆料？", a: "您可以通过网站顶部的「提交爆料」按钮或联系页面提交您的内幕线索。我们严格保护爆料人隐私，支持完全匿名爆料。请尽可能提供详细的证据材料。" },
                  { q: "爆料人的隐私如何保护？", a: "我们采用端到端加密技术保护爆料人信息，爆料人可以选择完全匿名。社区从不要求爆料人提供真实身份信息，所有沟通记录均加密存储，敏感信息定期清除。" },
                  { q: "黑料社区的盈利模式是什么？", a: "黑料社区坚持独立运营，不接受影响内容独立性的商业合作和广告投放。我们的收入来源包括用户自愿捐赠和高级会员订阅服务，所有收入均用于维持平台运营和调查团队建设。" },
                  { q: "我可以转载黑料社区的内容吗？", a: "欢迎转载，但请注明出处并保留原文链接。对于独家调查内容，转载前请联系我们获取授权。" },
                ].map((faq, i) => (
                  <details key={i} className="group bg-background-100 rounded-lg">
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <span className="font-heading font-semibold text-foreground-200 text-sm md:text-base pr-4">
                        {faq.q}
                      </span>
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform text-lg"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-foreground-500 text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-14 md:py-20">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-100 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  有内幕线索？有合作意向？欢迎随时联系我们
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="flex flex-col gap-5"
              >
                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="name" className="text-foreground-300 text-sm font-medium">
                      姓名 <span className="text-primary-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-foreground-200 text-sm placeholder-foreground-600 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all"
                      placeholder="您的姓名（可使用化名）"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="email" className="text-foreground-300 text-sm font-medium">
                      邮箱 <span className="text-primary-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-foreground-200 text-sm placeholder-foreground-600 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="subject" className="text-foreground-300 text-sm font-medium">
                    主题 <span className="text-primary-400">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    className="px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-foreground-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all cursor-pointer"
                  >
                    <option value="">请选择主题</option>
                    <option value="爆料提交">爆料提交</option>
                    <option value="合作咨询">合作咨询</option>
                    <option value="内容反馈">内容反馈</option>
                    <option value="版权投诉">版权投诉</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="message" className="text-foreground-300 text-sm font-medium">
                    留言内容 <span className="text-primary-400">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={6}
                    className="px-4 py-3 bg-background-100 border border-background-200/60 rounded-lg text-foreground-200 text-sm placeholder-foreground-600 focus:outline-none focus:ring-1 focus:ring-primary-500/50 focus:border-primary-500/50 transition-all resize-none"
                    placeholder="请详细描述您的线索或需求（不超过500字）"
                  ></textarea>
                  <span className="text-foreground-600 text-xs text-right">最多500字符</span>
                </div>

                <button
                  type="submit"
                  disabled={formStatus === "submitting"}
                  className="w-full py-3.5 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-950 rounded-lg font-semibold text-base transition-all duration-200 cursor-pointer"
                >
                  {formStatus === "submitting" ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-background-950/30 border-t-background-950 rounded-full animate-spin"></div>
                      提交中...
                    </span>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill mr-2 w-5 h-5 inline-flex items-center justify-center align-middle"></i>
                      提交信息
                    </>
                  )}
                </button>

                {formMessage && (
                  <div
                    className={`p-4 rounded-lg text-sm ${
                      formStatus === "success"
                        ? "bg-accent-500/10 text-accent-400 border border-accent-500/20"
                        : "bg-primary-500/10 text-primary-400 border border-primary-500/20"
                    }`}
                  >
                    {formMessage}
                  </div>
                )}
              </form>

              {/* Contact Info */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
                {[
                  { icon: "ri-mail-line", title: "电子邮箱", value: "contact@heiliao.community" },
                  { icon: "ri-telegram-line", title: "Telegram", value: "@heiliao_community" },
                  { icon: "ri-map-pin-line", title: "办公地址", value: "中国 · 北京" },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="flex items-center gap-3 p-4 bg-background-100 rounded-lg"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 flex-shrink-0">
                      <i className={`${item.icon} text-primary-400`}></i>
                    </div>
                    <div>
                      <div className="text-foreground-500 text-xs">{item.title}</div>
                      <div className="text-foreground-200 text-sm font-medium">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}